// 1. React imports
import { useState } from 'react';

// 2. Internal components
import { BrandCard } from '~/components/common';
import { AuthorizationActions } from './AuthorizationActions';
import { ClientInfoSection } from './ClientInfoSection';
import { LoadingSpinner } from '~/components/common/LoadingSpinner';
import { OAuthReconnectCard } from './OAuthReconnectCard';
import { CardContent } from '~/components/ui/card';
import { Field, FieldLabel } from '~/components/ui/field';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';

// 3. Type imports
import type { OAuthClientMetadata, OAuthClientSummary } from '~/api/oauth';
import type { TopicResponse } from '~/generated/api/model';

// 5. Custom hooks and utilities
import { CLIENT_TYPE_DISPLAY_NAMES } from '~/utils/connectionTypes';

// Sentinel value for the "no topic selected" option. Base UI's Select cannot
// represent an unselected/empty-string item value directly.
const NONE_TOPIC_VALUE = 'none';

/**
 * Props for OAuthConsentCard component.
 *
 * Per §TS.3.2: Descriptive props interface naming.
 */
export interface OAuthConsentCardProps {
  /** OAuth client ID requesting authorization */
  clientId: string | null;
  /** OAuth redirect URI where user will return after authorization */
  redirectUri: string;
  /** Scopes requested by the OAuth client */
  requestedScopes: string[];
  /** OAuth state parameter for CSRF protection */
  state: string | null;
  /** Available topics for the user to select from */
  topics: TopicResponse[];
  /** OAuth client metadata from backend */
  metadata: OAuthClientMetadata | null;
  /** Loading state for metadata fetch */
  isLoadingMetadata: boolean;
  /** Summary of existing authorized client (null if none exists) */
  existingClient: OAuthClientSummary | null;
  /** Loading state for checking existing client */
  isCheckingExisting: boolean;
  /** Callback when user authorizes access with selected topic */
  onAuthorize: (topicId: string) => void;
  /** Callback when user cancels authorization */
  onCancel: () => void;
  /** True while the authorize request is in flight */
  isAuthorizing?: boolean;
}

/**
 * OAuth consent card that displays client information, topic selector, and permission controls.
 *
 * Behavior:
 * - Shows LoadingSpinner when isCheckingExisting is true
 * - Shows OAuthReconnectCard when existingClient is present
 * - Shows consent form (topic selector + permissions) when existingClient is null
 * - Authorize button disabled until topic selected
 * - Cancel button triggers onCancel with access_denied error
 *
 * Per §TS.3.1: Function declaration for named export.
 * Per §TS.3.3: Explicit state typing with useState.
 *
 * @example
 * ```tsx
 * <OAuthConsentCard
 *   clientId="example-client"
 *   redirectUri="https://example.com/callback"
 *   requestedScopes={["read:messages", "write:messages"]}
 *   state="random-state-token"
 *   topics={userTopics}
 *   metadata={metadata}
 *   isLoadingMetadata={false}
 *   existingClient={null}
 *   isCheckingExisting={false}
 *   onAuthorize={(topicId) => handleAuthorize(topicId)}
 *   onCancel={() => handleCancel()}
 * />
 * ```
 */
export function OAuthConsentCard({
  clientId,
  redirectUri,
  requestedScopes,
  // state is used by parent but not needed here
  topics,
  metadata,
  isLoadingMetadata,
  existingClient,
  isCheckingExisting,
  onAuthorize,
  onCancel,
  isAuthorizing = false,
}: OAuthConsentCardProps) {
  // State for topic selection (per §TS.3.3)
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);

  // Derive clientType from metadata for ClientInfoSection display name lookup
  // Backend returns lowercase client_type like 'chatgpt' or 'claude'
  const clientType = metadata?.clientName?.toLowerCase() ?? null;

  // Handle topic selection change (per §TS.3.5)
  const handleTopicChange = (value: string) => {
    setSelectedTopicId(value === NONE_TOPIC_VALUE ? null : value);
  };

  // Handle authorize button click
  // Topic is optional - pass empty string if none selected
  const handleAuthorizeClick = () => {
    onAuthorize(selectedTopicId ?? '');
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    onCancel();
  };

  // Handle reconnect - reconnect with first topic from existing client
  const handleReconnectClick = () => {
    const firstTopicId = existingClient?.topics[0]?.id ?? '';
    onAuthorize(firstTopicId);
  };

  // Show loading spinner when checking for existing client (per §TS.8.1)
  if (isCheckingExisting) {
    return (
      <BrandCard accent="top">
        <CardContent>
          <LoadingSpinner message="Checking existing authorizations..." />
        </CardContent>
      </BrandCard>
    );
  }

  // Show reconnect card if existing client found
  if (existingClient) {
    return (
      <OAuthReconnectCard
        clientId={clientId}
        metadata={metadata}
        existingClient={existingClient}
        onReconnect={handleReconnectClick}
        onCancel={onCancel}
        isAuthorizing={isAuthorizing}
      />
    );
  }

  return (
    <>
      {/* Client Info Card */}
      <BrandCard accent="top">
        <CardContent>
          <ClientInfoSection
            clientId={clientId}
            clientType={clientType}
            metadata={metadata}
            isLoading={isLoadingMetadata}
            requestedScopes={requestedScopes}
            redirectUri={redirectUri}
          />
        </CardContent>
      </BrandCard>

      {/* Topic Selection Card */}
      <BrandCard accent="top" className="mt-6">
        <CardContent>
          <h2 className="mb-2 text-lg font-semibold">Create Connection (Optional)</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Create a {(clientType && CLIENT_TYPE_DISPLAY_NAMES[clientType]) || 'new'} connection for
            one of your Topics now. You can always create more connections to other Topics later
            from your DeadDrop dashboard.
          </p>
          <Field>
            <FieldLabel htmlFor="oauth-topic-select">Topic</FieldLabel>
            <Select
              value={selectedTopicId ?? NONE_TOPIC_VALUE}
              items={[
                { value: NONE_TOPIC_VALUE, label: 'None - skip for now' },
                ...topics.map((topic) => ({ value: topic.id, label: topic.name })),
              ]}
              onValueChange={(value) => {
                if (value !== null) handleTopicChange(value);
              }}
            >
              <SelectTrigger id="oauth-topic-select" className="w-full">
                <SelectValue placeholder="None - skip for now" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={NONE_TOPIC_VALUE}>None - skip for now</SelectItem>
                </SelectGroup>
                <SelectGroup>
                  {topics.map((topic) => (
                    <SelectItem key={topic.id} value={topic.id}>
                      <div>
                        <p className="text-base">{topic.name}</p>
                        {topic.description && (
                          <span className="text-xs text-muted-foreground">{topic.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
        </CardContent>
      </BrandCard>

      {/* Authorization Actions */}
      <AuthorizationActions
        onAuthorize={handleAuthorizeClick}
        onCancel={handleCancelClick}
        disabled={false}
        isAuthorizing={isAuthorizing}
      />
    </>
  );
}
